import React, { useState, useEffect } from 'react';
import { Gift, Sparkles, TrendingUp, DollarSign, Lock, Trophy, AlertCircle, History, X, Crown, Award, Clock } from 'lucide-react';
import axios from 'axios';

const InstantLottery = () => {
    const [gameState, setGameState] = useState('idle');
    const [revealedCards, setRevealedCards] = useState([]);
    const [cards, setCards] = useState(Array(12).fill(null));
    const [matchedSymbol, setMatchedSymbol] = useState(null);
    const [prize, setPrize] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [jackpotAmount, setJackpotAmount] = useState(0);
    const [balance, setBalance] = useState(0);
    const [ticketUuid, setTicketUuid] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [ticketHistory, setTicketHistory] = useState([]);
    const [stats, setStats] = useState(null);
    const [lotteryInfo, setLotteryInfo] = useState(null);
    const [recentWinners, setRecentWinners] = useState([]);

    const prizeSymbols = {
        jackpot: { icon: '💎', label: 'ДЖЕКПОТ', color: 'from-yellow-400 to-orange-500' },
        money_010: { icon: '💵', label: '0.10 USDT', color: 'from-green-400 to-emerald-500' },
        money_020: { icon: '💵', label: '0.20 USDT', color: 'from-green-400 to-emerald-500' },
        money_030: { icon: '💵', label: '0.30 USDT', color: 'from-green-400 to-emerald-500' },
        money_040: { icon: '💵', label: '0.40 USDT', color: 'from-green-400 to-emerald-500' },
        money_050: { icon: '💸', label: '0.50 USDT', color: 'from-blue-400 to-cyan-500' },
        money_060: { icon: '💸', label: '0.60 USDT', color: 'from-blue-400 to-cyan-500' },
        money_070: { icon: '💸', label: '0.70 USDT', color: 'from-blue-400 to-cyan-500' },
        money_080: { icon: '💸', label: '0.80 USDT', color: 'from-blue-400 to-cyan-500' },
        money_100: { icon: '💸', label: '1.00 USDT', color: 'from-blue-400 to-cyan-500' },
        money_200: { icon: '💰', label: '2.00 USDT', color: 'from-purple-400 to-pink-500' },
        money_300: { icon: '💰', label: '3.00 USDT', color: 'from-purple-400 to-pink-500' },
        money_400: { icon: '💰', label: '4.00 USDT', color: 'from-purple-400 to-pink-500' },
        money_500: { icon: '💰', label: '5.00 USDT', color: 'from-purple-400 to-pink-500' },
        money_600: { icon: '💰', label: '6.00 USDT', color: 'from-purple-400 to-pink-500' },
        money_700: { icon: '💰', label: '7.00 USDT', color: 'from-purple-400 to-pink-500' },
        money_800: { icon: '💰', label: '8.00 USDT', color: 'from-purple-400 to-pink-500' },
        money_900: { icon: '🤑', label: '9.00 USDT', color: 'from-red-400 to-rose-500' },
        money_1000: { icon: '🤑', label: '10.00 USDT', color: 'from-red-400 to-rose-500' },
        money_2000: { icon: '🤑', label: '20.00 USDT', color: 'from-red-400 to-rose-500' },
        money_3000: { icon: '🤑', label: '30.00 USDT', color: 'from-red-400 to-rose-500' },
        money_4000: { icon: '🤑', label: '40.00 USDT', color: 'from-red-400 to-rose-500' },
        money_5000: { icon: '🤑', label: '50.00 USDT', color: 'from-red-400 to-rose-500' },
        clone: { icon: '👥', label: 'Персональный клон', color: 'from-indigo-400 to-purple-500' },
        case: { icon: '🎁', label: 'Лайт Кейс 50$', color: 'from-pink-400 to-rose-500' },
    };

    useEffect(() => {
        loadLotteryInfo();
        loadLotteryStats();
    }, []);

    const loadLotteryInfo = async () => {
        try {
            const response = await axios.get('https://api.lite-com.ru/api/lottery/info', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });

            if (response.data.success) {
                setLotteryInfo(response.data.data);
                setJackpotAmount(response.data.data.jackpot_amount);
                setBalance(response.data.data.user_balance);
            }
        } catch (error) {
            console.error('Error loading lottery info:', error);
        }
    };

    const loadLotteryStats = async () => {
        try {
            const response = await axios.get('https://api.lite-com.ru/api/lottery/stats', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });

            if (response.data.success) {
                setStats(response.data.data);
                setRecentWinners(response.data.data.recent_winners || []);
            }
        } catch (error) {
            console.error('Error loading lottery stats:', error);
        }
    };

    const purchaseTicket = async () => {
        if (isProcessing || gameState !== 'idle') return;
        if (balance < 1) {
            alert('Недостаточно средств! Пополните баланс.');
            return;
        }

        setIsProcessing(true);

        try {
            const response = await axios.post('https://api.lite-com.ru/api/lottery/purchase-ticket', {}, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });

            if (response.data.success) {
                setBalance(response.data.data.new_balance);
                setJackpotAmount(response.data.data.jackpot_amount);
                setTicketUuid(response.data.data.ticket_uuid);

                setGameState('playing');
                setRevealedCards([]);
                setCards(Array(12).fill(null));
                setMatchedSymbol(null);
                setPrize(null);
            } else {
                alert(response.data.message || 'Ошибка при покупке билета');
            }
        } catch (error) {
            console.error('Error purchasing ticket:', error);
            alert(error.response?.data?.message || 'Произошла ошибка. Попробуйте позже.');
        } finally {
            setIsProcessing(false);
        }
    };

    const revealCard = async (index) => {
        if (isProcessing || gameState !== 'playing' || revealedCards.includes(index)) return;

        setIsProcessing(true);

        try {
            const response = await axios.post('https://api.lite-com.ru/api/lottery/reveal-card', {
                card_index: index,
                ticket_uuid: ticketUuid
            }, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });

            if (response.data.success) {
                const { symbol, is_winning, winning_symbol, prize, all_cards } = response.data.data;

                const newCards = [...cards];
                newCards[index] = symbol;
                setCards(newCards);

                const newRevealed = [...revealedCards, index];
                setRevealedCards(newRevealed);

                if (is_winning) {
                    setMatchedSymbol(winning_symbol);
                    setPrize(prize);

                    setTimeout(() => {
                        setGameState('revealing');
                        setCards(all_cards);
                        setRevealedCards(Array.from({ length: 12 }, (_, i) => i));

                        setTimeout(() => {
                            setGameState('finished');
                            loadLotteryInfo();
                            loadLotteryStats();
                        }, 1500);
                    }, 500);
                }
            }
        } catch (error) {
            console.error('Error revealing card:', error);
            alert(error.response?.data?.message || 'Произошла ошибка. Попробуйте позже.');
        } finally {
            setIsProcessing(false);
        }
    };

    const playAgain = () => {
        setGameState('idle');
        setRevealedCards([]);
        setCards(Array(12).fill(null));
        setMatchedSymbol(null);
        setPrize(null);
        setTicketUuid(null);
    };

    const loadHistory = async () => {
        try {
            const response = await axios.get('https://api.lite-com.ru/api/lottery/user-tickets', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });

            if (response.data.success) {
                setTicketHistory(response.data.data.tickets);
                setShowHistory(true);
            }
        } catch (error) {
            console.error('Error loading history:', error);
        }
    };
    const restoreTicket = async (ticket) => {
        if (ticket.status !== 'active') {
            alert('Этот билет уже завершен');
            return;
        }

        setIsProcessing(true);

        try {
            const response = await axios.get(`https://api.lite-com.ru/api/lottery/restore-ticket/${ticket.ticket_uuid}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });

            if (response.data.success) {
                const { revealed_cards, cards, ticket_uuid } = response.data.data;

                setTicketUuid(ticket_uuid);
                setCards(cards || Array(12).fill(null)); // ✅ Защита от undefined
                setRevealedCards(revealed_cards || []); // ✅ Защита от undefined
                setGameState('playing');
                setShowHistory(false);

                alert('Билет восстановлен! Продолжайте открывать карточки.');
            }
        } catch (error) {
            console.error('Error restoring ticket:', error);
            alert(error.response?.data?.message || 'Ошибка при восстановлении билета');
        } finally {
            setIsProcessing(false);
        }
    };
    const formatTimeAgo = (dateString) => {
        if (!dateString) return 'Недавно';

        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Только что';
        if (diffMins < 60) return `${diffMins} мин. назад`;
        if (diffHours < 24) return `${diffHours} ч. назад`;
        if (diffDays < 7) return `${diffDays} дн. назад`;

        return date.toLocaleDateString('ru-RU');
    };

    const getPrizeIcon = (prizeType) => {
        switch(prizeType) {
            case 'jackpot': return '💎';
            case 'clone': return '👥';
            case 'case': return '🎁';
            default: return '💰';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#d5ebe6] via-[#a7d7cd] to-[#2b6c8e] p-4 md:p-8">
            <div className="max-w-6xl mx-auto">
                {/* Заголовок и информация */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold text-[#2b6c8e] flex items-center gap-2">
                                <Sparkles className="w-8 h-8 text-yellow-500" />
                                Мгновенная Лотерея
                            </h1>
                            <p className="text-gray-600 mt-2">Открой 3 одинаковых символа и выиграй приз!</p>
                        </div>

                        <div className="flex flex-col gap-3">
                            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl p-4 text-white text-center">
                                <div className="flex items-center justify-center gap-2 mb-1">
                                    <Gift className="w-5 h-5" />
                                    <span className="text-sm font-semibold">ДЖЕКПОТ</span>
                                </div>
                                <div className="text-2xl font-bold">{jackpotAmount.toFixed(2)} USDT</div>
                            </div>

                            <div className="bg-gradient-to-r from-[#2b6c8e] to-[#a7d7cd] rounded-xl p-4 text-white text-center">
                                <div className="flex items-center justify-center gap-2 mb-1">
                                    <DollarSign className="w-5 h-5" />
                                    <span className="text-sm font-semibold">ВАШ БАЛАНС</span>
                                </div>
                                <div className="text-2xl font-bold">{balance.toFixed(2)} USDT</div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 flex justify-end">
                        <button
                            onClick={loadHistory}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <History className="w-4 h-4" />
                            <span className="text-sm font-medium">История билетов</span>
                        </button>
                    </div>
                </div>

                {/* Последний победитель джекпота */}
                {lotteryInfo?.last_jackpot_winner && (
                    <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-2xl shadow-2xl p-6 mb-6 text-white animate-pulse">
                        <div className="flex items-center gap-3 mb-3">
                            <Crown className="w-8 h-8 animate-bounce" />
                            <h3 className="text-2xl font-bold">Последний победитель ДЖЕКПОТА!</h3>
                        </div>
                        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-sm opacity-90">Игрок</p>
                                    <p className="text-xl font-bold">{lotteryInfo.last_jackpot_winner.login}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm opacity-90">Выигрыш</p>
                                    <p className="text-2xl font-bold">{lotteryInfo.last_jackpot_winner.amount.toFixed(2)} USDT</p>
                                </div>
                            </div>
                            {lotteryInfo.last_jackpot_winner.date && (
                                <p className="text-sm opacity-75 mt-2 flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {formatTimeAgo(lotteryInfo.last_jackpot_winner.date)}
                                </p>
                            )}
                        </div>
                    </div>
                )}



                {/* Игровое поле - Начальное состояние */}
                {gameState === 'idle' && (
                    <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
                        <div className="max-w-md mx-auto">
                            <Gift className="w-24 h-24 mx-auto text-[#2b6c8e] mb-6 animate-bounce" />
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">Готовы испытать удачу?</h2>
                            <p className="text-gray-600 mb-6">
                                Стоимость билета: <span className="font-bold text-[#2b6c8e]">1.00 USDT</span>
                            </p>
                            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 mb-8">
                                <p className="text-sm text-gray-700 space-y-2">
                                    <span className="block">🎯 Каждый билет гарантированно выигрышный!</span>
                                    <span className="block">🎁 Открой 3 одинаковых символа для получения приза</span>
                                    <span className="block">💎 Шанс выиграть джекпот: 0.5%</span>
                                </p>
                            </div>
                            <button
                                onClick={purchaseTicket}
                                disabled={isProcessing || balance < 1}
                                className="w-full bg-gradient-to-r from-[#2b6c8e] to-[#a7d7cd] text-white font-bold py-4 px-8 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <TrendingUp className="w-5 h-5" />
                                {isProcessing ? 'Обработка...' : 'Купить билет за 1 USDT'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Карточки - Игровое поле */}
                {(gameState === 'playing' || gameState === 'revealing' || gameState === 'finished') && (
                    <div className="bg-white rounded-2xl shadow-2xl p-6">
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-4 md:gap-6 mb-6">
                            {cards.map((symbol, index) => (
                                <button
                                    key={index}
                                    onClick={() => revealCard(index)}
                                    disabled={isProcessing || revealedCards.includes(index) || gameState !== 'playing'}
                                    className={`aspect-square rounded-2xl transition-all duration-300 transform ${
                                        revealedCards.includes(index)
                                            ? `bg-gradient-to-br ${prizeSymbols[symbol]?.color || 'from-gray-400 to-gray-500'} scale-105 shadow-2xl border-4 border-white`
                                            : 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 hover:scale-105 hover:shadow-2xl cursor-pointer shadow-lg'
                                    } ${
                                        matchedSymbol === symbol && revealedCards.includes(index)
                                            ? 'ring-4 ring-yellow-400 animate-pulse'
                                            : ''
                                    } disabled:cursor-not-allowed flex items-center justify-center relative overflow-hidden`}
                                    style={{
                                        boxShadow: revealedCards.includes(index)
                                            ? '0 10px 40px rgba(0,0,0,0.3)'
                                            : '0 8px 30px rgba(99, 102, 241, 0.4)'
                                    }}
                                >
                                    {revealedCards.includes(index) ? (
                                        <div className="text-center p-2 relative z-10">
                                            <div className="text-4xl md:text-5xl mb-2 drop-shadow-lg">{prizeSymbols[symbol]?.icon || '❓'}</div>
                                            <div className="text-xs md:text-sm text-white font-bold leading-tight drop-shadow-md px-1">
                                                {prizeSymbols[symbol]?.label || 'Unknown'}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="relative w-full h-full flex items-center justify-center">
                                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-2xl"></div>
                                            <div className="absolute top-0 left-0 w-full h-full">
                                                <div className="absolute top-2 left-2 w-16 h-16 bg-white/20 rounded-full blur-2xl"></div>
                                                <div className="absolute bottom-2 right-2 w-20 h-20 bg-white/10 rounded-full blur-3xl"></div>
                                            </div>
                                            <div className="relative z-10 flex flex-col items-center">
                                                <div className="relative">
                                                    <Lock className="w-10 h-10 md:w-12 md:h-12 text-white drop-shadow-lg" />
                                                    {gameState === 'playing' && (
                                                        <>
                                                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                                                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full"></div>
                                                        </>
                                                    )}
                                                </div>
                                                <div className="mt-2 text-xs text-white/80 font-semibold">?</div>
                                            </div>
                                            <div className="absolute inset-0 border-2 border-white/30 rounded-2xl pointer-events-none"></div>
                                            {gameState === 'playing' && (
                                                <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300">
                                                    <Sparkles className="absolute top-3 right-3 w-4 h-4 text-yellow-300 animate-pulse" />
                                                    <Sparkles className="absolute bottom-3 left-3 w-4 h-4 text-yellow-300 animate-pulse delay-75" />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>

                        {gameState === 'finished' && prize && (
                            <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-xl p-6 text-white text-center mb-6 animate-pulse">
                                <Sparkles className="w-16 h-16 mx-auto mb-4 animate-spin" />
                                <h2 className="text-3xl md:text-4xl font-bold mb-2">🎉 Поздравляем! 🎉</h2>
                                <p className="text-xl md:text-2xl mb-4">Вы выиграли:</p>
                                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mb-4">
                                    <p className="text-2xl md:text-3xl font-bold">{prize.label}</p>
                                    {prize.amount && prize.amount > 0 && (
                                        <p className="text-3xl md:text-4xl font-bold mt-2">{prize.amount} USDT</p>
                                    )}
                                </div>
                                <button
                                    onClick={playAgain}
                                    className="mt-4 bg-white text-[#2b6c8e] font-bold py-3 px-8 rounded-xl hover:bg-gray-100 transition-all transform hover:scale-105"
                                >
                                    Играть снова
                                </button>
                            </div>
                        )}

                        {gameState === 'playing' && (
                            <div className="text-center">
                                <div className="inline-block bg-gradient-to-r from-[#2b6c8e] to-[#a7d7cd] text-white px-6 py-3 rounded-full mb-2">
                                    <p className="font-semibold">Открыто карточек: {revealedCards.length} / 12</p>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">Найдите 3 одинаковых символа для выигрыша!</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Таблица призов */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 mt-6">
                    <h3 className="text-xl font-bold text-[#2b6c8e] mb-4 flex items-center gap-2">
                        <Trophy className="w-6 h-6" />
                        Таблица призов и вероятностей
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2">
                            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border-l-4 border-yellow-500">
                                <span className="flex items-center gap-2">
                                    <span className="text-2xl">💎</span>
                                    <span className="font-semibold">Джекпот</span>
                                </span>
                                <span className="font-bold text-yellow-600">0.5%</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                <span className="flex items-center gap-2">
                                    <span className="text-xl">💵</span>
                                    <span>0.10 - 0.40 USDT</span>
                                </span>
                                <span className="font-bold text-green-600">70%</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                <span className="flex items-center gap-2">
                                    <span className="text-xl">💸</span>
                                    <span>0.50 - 2.00 USDT</span>
                                </span>
                                <span className="font-bold text-blue-600">20%</span>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                <span className="flex items-center gap-2">
                                    <span className="text-xl">💰</span>
                                    <span>3.00 - 8.00 USDT</span>
                                </span>
                                <span className="font-bold text-purple-600">7%</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                <span className="flex items-center gap-2">
                                    <span className="text-xl">🤑</span>
                                    <span>9.00 - 50.00 USDT</span>
                                </span>
                                <span className="font-bold text-red-600">2%</span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-500">
                                <span className="flex items-center gap-2">
                                    <span className="text-xl">👥🎁</span>
                                    <span className="font-semibold">Спец. призы</span>
                                </span>
                                <span className="font-bold text-purple-600">0.5%</span>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Последние выигрыши */}
                {recentWinners.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-2xl p-6 mt-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Award className="w-6 h-6 text-[#2b6c8e]" />
                            <h3 className="text-xl font-bold text-[#2b6c8e]">Последние выигрыши</h3>
                        </div>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {recentWinners.slice(0, 10).map((winner, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center justify-between p-4 rounded-lg transition-all hover:shadow-md ${
                                        winner.prize_type === 'jackpot'
                                            ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500'
                                            : 'bg-gray-50'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="text-3xl">{getPrizeIcon(winner.prize_type)}</div>
                                        <div>
                                            <p className="font-semibold text-gray-800">{winner.user_login}</p>
                                            <p className="text-sm text-gray-600">{winner.prize_label}</p>
                                            {winner.won_at && (
                                                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                                    <Clock className="w-3 h-3" />
                                                    {formatTimeAgo(winner.won_at)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {winner.prize_amount > 0 && (
                                        <div className={`text-right font-bold ${
                                            winner.prize_type === 'jackpot'
                                                ? 'text-yellow-600 text-xl'
                                                : 'text-green-600'
                                        }`}>
                                            {winner.prize_amount.toFixed(2)} USDT
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {/* Модальное окно истории */}
                {showHistory && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
                            <div className="flex justify-between items-center p-6 border-b">
                                <h3 className="text-2xl font-bold text-[#2b6c8e]">История билетов</h3>
                                <button
                                    onClick={() => setShowHistory(false)}
                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto max-h-[60vh]">
                                {ticketHistory.length > 0 ? (
                                    <div className="space-y-4">
                                        {ticketHistory.map((ticket) => (
                                            <div key={ticket.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <p className="text-sm text-gray-600">
                                                            {new Date(ticket.created_at).toLocaleString('ru-RU')}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-1">ID: {ticket.ticket_uuid}</p>
                                                    </div>
                                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                        ticket.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                                    }`}>
                                                        {ticket.status === 'completed' ? 'Завершен' : 'Активен'}
                                                    </span>
                                                </div>
                                                {ticket.prize_description && (
                                                    <div className="bg-white rounded p-3 mt-2">
                                                        <p className="text-sm font-semibold text-gray-700">Выигрыш:</p>
                                                        <p className="text-lg font-bold text-[#2b6c8e]">{ticket.prize_description}</p>
                                                        {ticket.prize_amount > 0 && (
                                                            <p className="text-xl font-bold text-green-600 mt-1">
                                                                {parseFloat(ticket.prize_amount).toFixed(2)} USDT
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                                {ticket.status !== 'completed' && (
                                                    <button
                                                        onClick={() => restoreTicket(ticket)}
                                                        disabled={gameState !== 'idle'}
                                                        className={`w-full mt-3 py-2 px-4 rounded-lg font-semibold transition-all ${
                                                            gameState !== 'idle'
                                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                : 'bg-gradient-to-r from-[#2b6c8e] to-[#a7d7cd] text-white hover:shadow-lg'
                                                        }`}
                                                    >
                                                        {gameState !== 'idle'
                                                            ? '⚠️ Завершите текущий билет'
                                                            : '🎮 Вернуться к билету'
                                                        }
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-500">У вас пока нет купленных билетов</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InstantLottery;
